import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, Typography, TextField, Button, Alert, ThemeProvider, IconButton, InputAdornment } from '@mui/material';
import { Eye, EyeOff } from 'lucide-react';
import { getSupabaseClient } from '@lingaai/supabase-client';
import { lightTheme } from '@lingaai/shared-theme';
import { ICON_SIZES, ICON_STROKE } from '../../constants/icons';

export function SetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Supabase handles authentication via hash token automatically
  // No need to validate type - if user is here, they clicked a valid link

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const supabase = getSupabaseClient();

      // Update user password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) throw updateError;

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (user) {
        // Update user status from 'invited' to 'approved'
        const { error: statusError } = await supabase.from('users').update({ status: 'approved' }).eq('id', user.id).eq('status', 'invited');

        if (statusError) {
          console.error('Failed to update user status:', statusError);
          // Don't throw - password is set, status update is secondary
        }
      }

      setSuccess(true);

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <Box display="flex" minHeight="100vh" bgcolor="grey.100" sx={{ position: 'relative', overflow: 'hidden' }}>
        {/* Animated geometric background */}
        <Box
          component="svg"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 0,
            opacity: 0.6,
          }}
          viewBox="0 0 1200 800"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#174a43" stopOpacity="1">
                <animate attributeName="stop-color" values="#174a43; #22C55E; #174a43" dur="15s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#22C55E" stopOpacity="0.5">
                <animate attributeName="stop-color" values="#22C55E; #10B981; #22C55E" dur="15s" repeatCount="indefinite" />
              </stop>
            </linearGradient>
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F59E0B" stopOpacity="1">
                <animate attributeName="stop-color" values="#F59E0B; #FCD34D; #F59E0B" dur="18s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#FCD34D" stopOpacity="0.5">
                <animate attributeName="stop-color" values="#FCD34D; #F59E0B; #FCD34D" dur="18s" repeatCount="indefinite" />
              </stop>
            </linearGradient>
          </defs>
          <polygon points="0,0 600,100 400,500" fill="url(#gradient1)" opacity="0.15" style={{ animation: 'morph1 20s ease-in-out infinite' }} />
          <polygon points="1200,0 800,300 1200,600" fill="url(#gradient2)" opacity="0.12" style={{ animation: 'morph2 25s ease-in-out infinite' }} />
        </Box>
        <style>
          {`
          @keyframes morph1 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(50px, -30px); }
          }
          @keyframes morph2 {
            0%, 100% { transform: translate(0, 0); }
            50% { transform: translate(-40px, 40px); }
          }
        `}
        </style>

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1,
            p: 3,
          }}
        >
          <Card sx={{ p: 4, maxWidth: 450, width: '100%' }}>
            {success ? (
              <Alert severity="success">Password set successfully! Redirecting to dashboard...</Alert>
            ) : (
              <>
                <Typography variant="h3" gutterBottom align="center" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                  Welcome to Linga AI
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                  Set your password to get started
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    margin="normal"
                    helperText="Must be at least 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                            {showPassword ? <EyeOff size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Eye size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                    margin="normal"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                            {showConfirmPassword ? <EyeOff size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} /> : <Eye size={ICON_SIZES.lg} strokeWidth={ICON_STROKE} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button type="submit" fullWidth variant="contained" size="large" disabled={isLoading || !password || !confirmPassword} sx={{ mt: 3 }}>
                    {isLoading ? 'Setting Password...' : 'Set Password & Continue'}
                  </Button>
                </form>
              </>
            )}
          </Card>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
